import React, { useMemo, useContext } from "react";

import { renderMoney, renderColumn, getRangeByNumber, getAccountInfoFromSession } from "utils/helpers";
import { SCREEN_MEDIA, LOCAL_STORAGE_USER_INFO_DIALER } from "const";
import { LS_DIALER_NUMBERS_PER_PAGE } from "const/localStorageKeys";
import { useWindowWidth } from "hooks";
import CustomizableTable from "components/client/CustomizableTable/CustomizableTable";

import { RtlContext } from "../../App";
import m from "definedMessages";

const { md } = SCREEN_MEDIA;

export default ({
    data = [],
    service,
    loading,
    page,
    count,
    perPage,

    tableRef,
    currentDisplay,

    getRowKey,
    getItems,
    setSelectedData,
    useSelectedData,

    formatMessage
}) => {

    const windowWidth = useWindowWidth();
    const rtl = useContext(RtlContext);
    const isMobile = windowWidth < md.min;

    let storagedAccountInfo = localStorage.getItem(LOCAL_STORAGE_USER_INFO_DIALER);
    storagedAccountInfo = storagedAccountInfo !== null ? JSON.parse(storagedAccountInfo) : null;
    const columns = useMemo(() => {
        const desktopColumns = [
            {
                id: "zone",
                label: formatMessage(m.zone),
                value: (({ worldzone_name }) => worldzone_name || "None"),
                minWidth: 110,
                flexGrow: 4,
                align: "center",
                displayWhen: ["trunk_number"],
            },
            {
                id: "range",
                label: formatMessage(m.range),
                value: (({ name, price_range }) => price_range
                    ? price_range.name || ""
                    : name || "0"),
                minWidth: 200,
                flexGrow: 4,
                align: "center",
                displayWhen: ["trunk_number", "price_range"],
            },
            {
                id: "number",
                label: formatMessage(m.number),
                value: (({ number, numbers_count }) => getRangeByNumber(number, numbers_count)),
                minWidth: 130,
                flexGrow: 3,
                align: "center",
                displayWhen: ["trunk_number"],
            },
            ...(service ? [{
                id: "bill_increment",
                label: formatMessage(m.billingIncrement),
                dataKey: "rrc_name",
                minWidth: 150,
                flexGrow: 2,
                align: "center",
                displayWhen: ["trunk_number"],
            }] : []),
            {
                id: "start_date",
                label: formatMessage(m.start_date),
                dataKey: "start_date",
                minWidth: 130,
                flexGrow: 3,
                align: "center",
                displayWhen: ["trunk_number"],
            },
            {
                id: "rate",
                label: formatMessage(m.rate),
                value: (({ rate }) => {
                    const accountInfo = storagedAccountInfo && storagedAccountInfo.session ? getAccountInfoFromSession(storagedAccountInfo.session, service) : {};
                    const currencyName = "currency_name" in accountInfo ? accountInfo.currency_name : "";
                    return renderMoney(rate, currencyName, 3);
                }),
                minWidth: 90,
                flexGrow: 2,
                align: "center",
                displayWhen: ["trunk_number", "price_range", "subdestination"],
            },
            {
                label: formatMessage(m.allocatedNumbers),
                dataKey: "trunk_numbers",
                minWidth: 90,
                flexGrow: 2,
                displayWhen: ["price_range"],
                align: !rtl ? "right" : "left"
            },
            {
                label: formatMessage(m.subdestination),
                dataKey: "subdestination_name",
                minWidth: 200,
                flexGrow: 4,
                align: !rtl ? "left" : "right",
                displayWhen: ["subdestination"]
            },
            {
                label: formatMessage(m.allocatedNumbersAndRanges),
                // dataKey: "allocated_numbers_and_ranges",
                value: ({ trunk_numbers, price_ranges }) => `${trunk_numbers || 0}/${price_ranges || 0}`,
                minWidth: 90,
                flexGrow: 2,
                displayWhen: ["subdestination"],
                align: !rtl ? "right" : "left"
            },
        ];

        const mobileColumns = [
            {
                id: "zone_range",
                label: `${formatMessage(m.zone)} / ${formatMessage(m.range)}`,
                value: (({ worldzone_name, name }) => {
                    return (
                        <div className="table-two-staged__cell">
                            <div className="table-two-staged__cell-first">{worldzone_name || "None"}</div>
                            <div className="table-two-staged__cell-second">
                                {name || "0"}
                            </div>
                        </div>
                    )
                }),
                minWidth: 180,
                flexGrow: 4,
                align: "center",
                displayWhen: ["trunk_number"],
            },
            {
                id: "range",
                label: formatMessage(m.range),
                value: (({ name, price_range }) => price_range
                    ? price_range.name || ""
                    : name || "0"),
                minWidth: 200,
                flexGrow: 4,
                align: "center",
                displayWhen: ["price_range"],
            },
            {
                id: "number",
                label: formatMessage(m.number),
                value: (({ number, numbers_count }) => getRangeByNumber(number, numbers_count)),
                minWidth: 140,
                flexGrow: 3,
                align: "center",
                displayWhen: ["trunk_number"],
            },
            {
                id: "rate",
                label: `${formatMessage(m.rate)}`,
                value: (({ rate, bill_increment }) => {
                    const accountInfo = storagedAccountInfo && storagedAccountInfo.session ? getAccountInfoFromSession(storagedAccountInfo.session, service) : {};
                    const currencyName = "currency_name" in accountInfo ? accountInfo.currency_name : "";
                    return (
                        <div className="table-two-staged__cell">
                            <div className="table-two-staged__cell-first">{bill_increment}</div>
                            <div className="table-two-staged__cell-second">
                                {renderMoney(rate, currencyName, 3)}
                            </div>
                        </div>
                    )
                }),
                minWidth: 150,
                flexGrow: 2,
                align: "center",
                displayWhen: ["trunk_number"],
            },
            {
                id: "start_date",
                label: formatMessage(m.start_date),
                dataKey: "start_date",
                minWidth: 130,
                flexGrow: 3,
                align: "center",
                displayWhen: ["trunk_number"],
            },
            {
                id: "rate",
                label: formatMessage(m.rate),
                value: (({ rate }) => {
                    const accountInfo = storagedAccountInfo && storagedAccountInfo.session ? getAccountInfoFromSession(storagedAccountInfo.session, service) : {};
                    const currencyName = "currency_name" in accountInfo ? accountInfo.currency_name : "";
                    return renderMoney(rate, currencyName, 3);
                }),
                minWidth: 90,
                flexGrow: 2,
                align: "center",
                displayWhen: ["price_range", "subdestination"],
            },
            {
                label: formatMessage(m.allocatedNumbers),
                dataKey: "trunk_numbers",
                minWidth: 90,
                flexGrow: 2,
                displayWhen: ["price_range"],
                align: !rtl ? "right" : "left"
            },
            {
                label: formatMessage(m.subdestination),
                dataKey: "subdestination_name",
                minWidth: 200,
                flexGrow: 4,
                displayWhen: ["subdestination"],
                align: !rtl ? "left" : "right",
            },
            {
                label: formatMessage(m.allocatedNumbersAndRanges),
                value: ({ trunk_numbers, price_ranges }) => `${trunk_numbers || 0}/${price_ranges || 0}`,
                minWidth: 90,
                flexGrow: 2,
                displayWhen: ["subdestination"],
                align: !rtl ? "right" : "left"
            },
        ];

        const renderedColumns = !isMobile
            ? desktopColumns.filter(el => el.displayWhen.indexOf(currentDisplay) !== -1).map(renderColumn)
            : mobileColumns.filter(el => el.displayWhen.indexOf(currentDisplay) !== -1).map(renderColumn);

        return renderedColumns;
    }, [currentDisplay, service, isMobile]);

    return (
        <>
            <CustomizableTable
                {...{
                    rtl,
                    data,
                    count,
                    page,
                    perPage,
                    loading,
                    getItems,
                    renderedColumns: columns,
                    useSelectedData
                }}
                ref={tableRef}
                setSelected={setSelectedData}
                data={data}
                row_key={getRowKey()}
                isSelect={true}
                isSelectedAll={true}
                isChangedColumns={false}
                isPaginated={true}
                defaultPerPage={10}
                perPageLSKey={LS_DIALER_NUMBERS_PER_PAGE}
                calculatedOuterHeight={601}
                isMobile={isMobile}
            />
        </>
    );
};